import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const emptyFields = occurredOn => ({
  occurredOn,
  serviceUserId:"",
  itemCode:"",
  description:"",
  amount:"",
  quantityValue:"",
  unitPrice:"",
  reason:"",
});

const inputStyle = {
  width:"100%", minHeight:46, boxSizing:"border-box", padding:"9px 11px",
  border:"1px solid #cbd5e1", borderRadius:8, background:"#fff", fontSize:16,
};
const labelStyle = { display:"block", color:"#334155", fontSize:12, fontWeight:800, marginBottom:5 };
const money = value => `${Number(value || 0).toLocaleString("ja-JP")}円`;
const statusLabel = state => state === "cancelled" ? "取消済み" : "有効";

export function jippiDailyToday() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:"Asia/Tokyo", year:"numeric", month:"2-digit", day:"2-digit",
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map(part=>[part.type,part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

export function jippiDailyRequestId() {
  if (typeof crypto === "undefined" || typeof crypto.randomUUID !== "function") {
    throw new Error("安全なrequest IDを生成できないため登録を開始できません");
  }
  return crypto.randomUUID();
}

export function jippiDailyErrorMessage(error) {
  const code = error?.payload?.reason_code || error?.reason_code || "";
  const messages = {
    building_assignment_not_found:"発生日に有効な所属建物を特定できません。管理者へ所属履歴の確認を依頼してください。",
    building_assignment_ambiguous:"発生日の所属建物が複数あります。推測登録は行わず、管理者へ所属履歴の確認を依頼してください。",
    billing_month_closed:"対象月は締め済みのため登録・修正・取消できません。",
    billing_rule_not_found:"発生日に有効な費目ではありません。対象日と費目を確認してください。",
    billing_rule_ambiguous:"有効な費目設定が重複しています。管理者へ設定確認を依頼してください。",
    service_user_out_of_scope:"この施設の操作範囲外の利用者です。",
    record_out_of_scope:"この施設の操作範囲外の実費明細です。",
    revision_conflict:"他の人によって内容が変更されています。最新状態を確認してください。",
    request_id_conflict:"同じ送信識別子で異なる内容が送信されました。入力内容を確認してからやり直してください。",
    entry_cancelled:"この実費明細はすでに取消済みです。最新状態を確認してください。",
  };
  if (messages[code]) return messages[code];
  if (error?.status === 403) return "この操作を行う権限がありません。施設と権限を確認してください。";
  return error?.message || "日常実費の処理に失敗しました。入力内容を保持しています。";
}

function fieldSummary(form, selectedUser, selectedItem) {
  return [
    ["利用者",selectedUser?.name || "—"],
    ["発生日",form.occurredOn || "—"],
    ["費目",selectedItem?.item_label || "—"],
    ["内容",form.description || "—"],
    ["金額",money(form.amount)],
  ];
}

export default function JippiDailyEntry({
  store,
  request,
  access,
  requestIdFactory = jippiDailyRequestId,
  initialDate = jippiDailyToday(),
}) {
  const facilities = useMemo(()=>Array.isArray(access?.facilities) ? access.facilities : [],[access]);
  const initialFacility = facilities.find(facility=>facility.id === store?.id) || facilities[0] || null;
  const [facilityId,setFacilityId] = useState(initialFacility?.id || "");
  const [form,setForm] = useState(()=>emptyFields(initialDate));
  const [options,setOptions] = useState({ service_users:[], billing_policy:null });
  const [recent,setRecent] = useState([]);
  const [lastCreated,setLastCreated] = useState(null);
  const [loadingOptions,setLoadingOptions] = useState(false);
  const [loadingRecent,setLoadingRecent] = useState(false);
  const [editing,setEditing] = useState(null);
  const [confirmationOpen,setConfirmationOpen] = useState(false);
  const [cancelTarget,setCancelTarget] = useState(null);
  const [cancelReason,setCancelReason] = useState("");
  const [pendingRequestId,setPendingRequestId] = useState("");
  const [saving,setSaving] = useState(false);
  const [error,setError] = useState("");
  const [notice,setNotice] = useState("");
  const savingRef = useRef(false);

  useEffect(()=>{
    const next = facilities.find(facility=>facility.id === facilityId)
      || facilities.find(facility=>facility.id === store?.id)
      || facilities[0];
    if (next && next.id !== facilityId) setFacilityId(next.id);
  },[facilities,facilityId,store?.id]);

  const facility = facilities.find(candidate=>candidate.id === facilityId) || null;
  const capabilities = facility?.capabilities || {};
  const selectedUser = options.service_users?.find(user=>user.id === form.serviceUserId) || null;
  const selectedItem = selectedUser?.items?.find(item=>item.item_code === form.itemCode) || null;
  const billingProhibited = Boolean(options.billing_policy?.billing_prohibited);

  const loadOptions = useCallback(async()=>{
    if (!facilityId || !form.occurredOn) return;
    setLoadingOptions(true);
    try {
      const data = await request(`/jippi_daily_entries.php?action=options&storeId=${encodeURIComponent(facilityId)}&occurred_on=${encodeURIComponent(form.occurredOn)}`);
      setOptions({ service_users:Array.isArray(data.service_users) ? data.service_users : [], billing_policy:data.billing_policy || null });
    } catch (requestError) {
      setOptions({ service_users:[], billing_policy:null });
      setError(jippiDailyErrorMessage(requestError));
    } finally { setLoadingOptions(false); }
  },[facilityId,form.occurredOn,request]);

  const loadRecent = useCallback(async()=>{
    if (!facilityId) return;
    setLoadingRecent(true);
    try {
      const data = await request(`/jippi_daily_entries.php?action=recent&storeId=${encodeURIComponent(facilityId)}&limit=12`);
      setRecent(Array.isArray(data.records) ? data.records : []);
    } catch (requestError) {
      setError(jippiDailyErrorMessage(requestError));
    } finally { setLoadingRecent(false); }
  },[facilityId,request]);

  useEffect(()=>{ loadOptions(); },[loadOptions]);
  useEffect(()=>{ loadRecent(); },[loadRecent]);

  const resetSensitiveFields = useCallback(()=>{
    setForm(current=>emptyFields(current.occurredOn));
    setEditing(null);
    setPendingRequestId("");
  },[]);

  const changeField = (field,value) => {
    setForm(current=>({ ...current,[field]:value }));
    setPendingRequestId("");
    setNotice("");
    if (field !== "reason") setError("");
  };

  const openConfirmation = event => {
    event.preventDefault();
    if (!capabilities.can_create && !editing) { setError("この施設で実費を登録する権限がありません。"); return; }
    if (!capabilities.can_edit && editing) { setError("この施設で実費を修正する権限がありません。"); return; }
    if (!selectedUser || !selectedItem || !form.description.trim() || !/^\d+$/.test(String(form.amount)) || Number(form.amount) <= 0) {
      setError("利用者・発生日・費目・内容・1円以上の金額を入力してください。");
      return;
    }
    if (editing && !form.reason.trim()) { setError("修正理由を入力してください。"); return; }
    try {
      setPendingRequestId(current=>current || requestIdFactory());
      setConfirmationOpen(true);
      setError("");
    } catch (requestError) { setError(jippiDailyErrorMessage(requestError)); }
  };

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const body = {
      storeId:facilityId,
      occurred_on:form.occurredOn,
      item_code:form.itemCode,
      description:form.description.trim(),
      amount:Number(form.amount),
      quantity_value:form.quantityValue === "" ? null : form.quantityValue,
      unit_price:form.unitPrice === "" ? null : Number(form.unitPrice),
      request_id:pendingRequestId,
    };
    try {
      if (editing) {
        await request("/jippi_daily_entries.php","PUT",{
          action:"update", id:editing.id, expected_revision:editing.revision,
          ...body, reason:form.reason.trim(),
        });
        setNotice("実費明細を修正しました。月末確認が済んでいた場合は再確認必要になります。");
      } else {
        const response = await request("/jippi_daily_entries.php","POST",{ ...body,service_user_id:form.serviceUserId });
        setLastCreated(response?.record || null);
        setNotice("実費を登録しました。続けて次の実費を登録できます。");
      }
      setConfirmationOpen(false);
      resetSensitiveFields();
      await Promise.all([loadRecent(),loadOptions()]);
    } catch (requestError) {
      setError(jippiDailyErrorMessage(requestError));
      setConfirmationOpen(false);
      if ((requestError?.payload?.reason_code || requestError?.reason_code) === "revision_conflict") await loadRecent();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const beginEdit = record => {
    setEditing(record);
    setForm({
      occurredOn:record.occurred_on,
      serviceUserId:record.service_user_id,
      itemCode:record.item_code,
      description:record.description,
      amount:String(record.amount),
      quantityValue:record.quantity_value ?? "",
      unitPrice:record.unit_price ?? "",
      reason:"",
    });
    setPendingRequestId(""); setError(""); setNotice("");
    window.scrollTo?.({ top:0,behavior:"smooth" });
  };

  const openCancel = record => {
    try { setPendingRequestId(requestIdFactory()); }
    catch (requestError) { setError(jippiDailyErrorMessage(requestError)); return; }
    setCancelTarget(record); setCancelReason(""); setError(""); setNotice("");
  };

  const cancelRecord = async () => {
    if (savingRef.current || !cancelTarget || !cancelReason.trim()) return;
    savingRef.current = true; setSaving(true);
    try {
      await request("/jippi_daily_entries.php","PUT",{
        action:"cancel", id:cancelTarget.id, storeId:facilityId,
        expected_revision:cancelTarget.revision, cancel_reason:cancelReason.trim(),
        request_id:pendingRequestId,
      });
      setCancelTarget(null); setCancelReason(""); setPendingRequestId("");
      setNotice("実費明細を取消しました。履歴は監査用に保持されます。");
      await loadRecent();
    } catch (requestError) {
      setError(jippiDailyErrorMessage(requestError));
      if ((requestError?.payload?.reason_code || requestError?.reason_code) === "revision_conflict") await loadRecent();
    } finally { savingRef.current=false; setSaving(false); }
  };

  if (!facility) return <div role="alert">日常実費を閲覧できる施設がありません。</div>;

  return <section aria-label="日常実費入力">
    <style>{`.jippi-daily-layout{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,420px),1fr));gap:18px;align-items:start}.jippi-daily-layout>*{min-width:0}.jippi-daily-fields{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,max(240px,calc((100% - 12px)/2))),1fr));gap:12px}.jippi-daily-fields>*{min-width:0}.jippi-daily-span{grid-column:1/-1}`}</style>
    <div style={{ marginBottom:14 }}>
      <h2 style={{ margin:"0 0 5px",fontSize:22,color:store.color }}>実費を登録</h2>
      <div style={{ color:"#475569",fontSize:13,lineHeight:1.55 }}>実費が発生した時点で1件ずつ登録します。月末の確認・確定・締め処理は「実費請求」から行います。</div>
    </div>
    {billingProhibited && <div role="status" style={{ marginBottom:14, padding:"10px 12px", border:"1px solid #f59e0b", borderRadius:8, background:"#fffbeb", color:"#92400e", fontWeight:800 }}>2026年9月は練習月・請求禁止です。実費の入力・保存・修正はできますが、正式請求には進みません。</div>}
    <div className="jippi-daily-layout">
      <div style={{ background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:16 }}>
        <form onSubmit={openConfirmation}>
          <div className="jippi-daily-fields">
            {facilities.length > 1 && <label><span style={labelStyle}>施設</span><select aria-label="日常実費施設" value={facilityId} onChange={event=>{setFacilityId(event.target.value);setForm(emptyFields(form.occurredOn));setEditing(null);}} style={inputStyle}>{facilities.map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</select></label>}
            <label><span style={labelStyle}>発生日</span><input aria-label="日常実費発生日" type="date" value={form.occurredOn} onChange={event=>{setForm(current=>({ ...emptyFields(event.target.value),occurredOn:event.target.value }));setEditing(null);setPendingRequestId("");}} required style={inputStyle}/></label>
            <label><span style={labelStyle}>利用者</span><select aria-label="日常実費利用者" value={form.serviceUserId} onChange={event=>{changeField("serviceUserId",event.target.value);changeField("itemCode","");}} disabled={loadingOptions} required style={inputStyle}><option value="">選択してください</option>{options.service_users.map(user=><option key={user.id} value={user.id}>{user.name}</option>)}</select></label>
            <label><span style={labelStyle}>費目</span><select aria-label="日常実費費目" value={form.itemCode} onChange={event=>changeField("itemCode",event.target.value)} disabled={!selectedUser} required style={inputStyle}><option value="">選択してください</option>{(selectedUser?.items || []).map(item=><option key={item.item_code} value={item.item_code}>{item.item_label}</option>)}</select></label>
            <label className="jippi-daily-span"><span style={labelStyle}>内容</span><input aria-label="日常実費内容" value={form.description} onChange={event=>changeField("description",event.target.value)} maxLength={500} placeholder="例：表示された費目に対応する内容" required style={inputStyle}/></label>
            <label><span style={labelStyle}>金額（円）</span><input aria-label="日常実費金額" type="number" min="1" step="1" value={form.amount} onChange={event=>changeField("amount",event.target.value)} required style={inputStyle}/></label>
            <label><span style={labelStyle}>数量（任意）</span><input aria-label="日常実費数量" type="number" min="0" step="0.01" value={form.quantityValue} onChange={event=>changeField("quantityValue",event.target.value)} style={inputStyle}/></label>
            <label><span style={labelStyle}>単価（任意）</span><input aria-label="日常実費単価" type="number" min="0" step="1" value={form.unitPrice} onChange={event=>changeField("unitPrice",event.target.value)} style={inputStyle}/></label>
            {editing && <label className="jippi-daily-span"><span style={labelStyle}>修正理由</span><input aria-label="日常実費修正理由" value={form.reason} onChange={event=>changeField("reason",event.target.value)} maxLength={500} required style={inputStyle}/></label>}
          </div>
          <div style={{ marginTop:12,padding:"9px 11px",background:"#f8fafc",borderRadius:8,color:"#475569",fontSize:12 }}>金額は入力値を正本として登録します。数量×単価による自動計算は行いません。</div>
          {error && <div role="alert" style={{ marginTop:12,color:"#991b1b",background:"#fff1f2",padding:"10px 12px",borderRadius:8,fontWeight:800 }}>{error}</div>}
          {notice && <div role="status" style={{ marginTop:12,color:"#166534",background:"#f0fdf4",padding:"10px 12px",borderRadius:8,fontWeight:800 }}>{notice}</div>}
          <div style={{ display:"flex",gap:8,marginTop:14 }}>
            {editing && <button type="button" onClick={resetSensitiveFields} style={{ minHeight:48,flex:1 }}>修正をやめる</button>}
            <button type="submit" disabled={saving || (editing ? !capabilities.can_edit : !capabilities.can_create)} style={{ minHeight:50,flex:2,border:0,borderRadius:8,background:store.color,color:"#fff",fontWeight:900,fontSize:16 }}>{editing ? "修正内容を確認" : "登録内容を確認"}</button>
          </div>
        </form>
      </div>
      <aside aria-label="最近の実費明細（発生日順）" style={{ background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:16 }}>
        {lastCreated && <section aria-label="今回登録した実費" style={{ marginBottom:16,border:"2px solid #86efac",borderRadius:10,padding:"12px 13px",background:"#f0fdf4" }}>
          <h3 style={{ margin:"0 0 4px",fontSize:17,color:"#166534" }}>今回登録した実費</h3>
          <div style={{ color:"#475569",fontSize:11,lineHeight:1.5,marginBottom:9 }}>登録した内容です。金額や発生日に誤りがないか確認してください。</div>
          <dl style={{ display:"grid",gap:6,margin:0 }}>
            {[
              ["利用者",lastCreated.user_name],
              ["発生日",lastCreated.occurred_on],
              ["費目",lastCreated.item],
              ["内容",lastCreated.description],
              ["金額",money(lastCreated.amount)],
              ...(lastCreated.quantity_value !== null && lastCreated.quantity_value !== "" ? [["数量",String(lastCreated.quantity_value)]] : []),
              ...(lastCreated.unit_price !== null && lastCreated.unit_price !== "" ? [["単価",money(lastCreated.unit_price)]] : []),
            ].map(([label,value])=><div key={label} style={{ display:"grid",gridTemplateColumns:"64px minmax(0,1fr)",gap:8,padding:"6px 8px",background:"#fff",borderRadius:7 }}><dt style={{ color:"#64748b",fontSize:11 }}>{label}</dt><dd style={{ margin:0,fontWeight:900,overflowWrap:"anywhere" }}>{value}</dd></div>)}
          </dl>
        </section>}
        <div style={{ display:"flex",justifyContent:"space-between",gap:8,alignItems:"center",marginBottom:6 }}><h3 style={{ margin:0,fontSize:17 }}>最近の実費明細（発生日順）</h3><button type="button" onClick={loadRecent} disabled={loadingRecent}>再読込</button></div>
        <div style={{ color:"#64748b",fontSize:11,marginBottom:10 }}>発生日が新しい順に表示しています。</div>
        <div style={{ display:"grid",gap:8 }}>
          {recent.map(record=><article key={record.id} aria-label={`日常実費 ${record.user_name}`} style={{ border:"1px solid #e2e8f0",borderRadius:9,padding:"10px 11px",opacity:record.entry_state === "cancelled" ? .72 : 1 }}>
            <div style={{ display:"flex",justifyContent:"space-between",gap:10 }}><div><b>{record.user_name}</b><div style={{ marginTop:3,color:"#64748b",fontSize:11 }}>{record.occurred_on}・{record.item}</div></div><div style={{ textAlign:"right" }}><b>{money(record.amount)}</b><div style={{ color:record.entry_state === "cancelled" ? "#991b1b" : "#166534",fontSize:11,fontWeight:800 }}>{statusLabel(record.entry_state)}</div></div></div>
            <div style={{ marginTop:5,fontSize:12 }}>{record.description}</div>
            {record.entry_state !== "cancelled" && (capabilities.can_edit || capabilities.can_cancel) && <div style={{ display:"flex",gap:7,marginTop:9 }}>
              {capabilities.can_edit && <button type="button" onClick={()=>beginEdit(record)}>修正</button>}
              {capabilities.can_cancel && <button type="button" onClick={()=>openCancel(record)} style={{ color:"#991b1b" }}>取消</button>}
            </div>}
          </article>)}
          {!loadingRecent && recent.length === 0 && <div style={{ color:"#94a3b8",textAlign:"center",padding:20 }}>表示できる実費明細はありません</div>}
          {loadingRecent && <div style={{ color:"#64748b",padding:12 }}>読み込み中…</div>}
        </div>
      </aside>
    </div>
    {confirmationOpen && <div role="presentation" style={{ position:"fixed",inset:0,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,background:"rgba(15,23,42,.58)" }}><div role="dialog" aria-modal="true" aria-labelledby="jippi-daily-confirm-title" style={{ width:"100%",maxWidth:430,boxSizing:"border-box",background:"#fff",borderRadius:12,padding:18 }}>
      <h3 id="jippi-daily-confirm-title" style={{ margin:0,fontSize:19 }}>{editing ? "この内容で修正しますか？" : "この実費を登録しますか？"}</h3>
      <dl style={{ display:"grid",gap:7,margin:"14px 0 0" }}>{fieldSummary(form,selectedUser,selectedItem).map(([label,value])=><div key={label} style={{ display:"grid",gridTemplateColumns:"72px minmax(0,1fr)",gap:8,padding:"8px 10px",background:"#f8fafc",borderRadius:7 }}><dt style={{ color:"#64748b",fontSize:11 }}>{label}</dt><dd style={{ margin:0,fontWeight:900,overflowWrap:"anywhere" }}>{value}</dd></div>)}</dl>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginTop:16 }}><button type="button" disabled={saving} onClick={()=>setConfirmationOpen(false)} style={{ minHeight:48 }}>戻る／修正</button><button type="button" disabled={saving} onClick={save} style={{ minHeight:48,border:0,borderRadius:8,background:store.color,color:"#fff",fontWeight:900 }}>{saving ? "送信中…" : (editing ? "修正する" : "登録する")}</button></div>
    </div></div>}
    {cancelTarget && <div role="presentation" style={{ position:"fixed",inset:0,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,background:"rgba(15,23,42,.58)" }}><div role="dialog" aria-modal="true" aria-labelledby="jippi-daily-cancel-title" style={{ width:"100%",maxWidth:430,boxSizing:"border-box",background:"#fff",borderRadius:12,padding:18 }}>
      <h3 id="jippi-daily-cancel-title" style={{ margin:0,fontSize:19 }}>この実費明細を取消しますか？</h3><p style={{ color:"#475569",fontSize:12,lineHeight:1.6 }}>物理削除はせず、取消履歴を監査用に保持します。</p>
      <div style={{ padding:10,background:"#fff7ed",borderRadius:8,fontWeight:800 }}>{cancelTarget.user_name}・{cancelTarget.occurred_on}・{money(cancelTarget.amount)}</div>
      <label style={{ display:"block",marginTop:12 }}><span style={labelStyle}>取消理由</span><textarea aria-label="日常実費取消理由" value={cancelReason} onChange={event=>setCancelReason(event.target.value)} maxLength={500} rows={3} required style={{ ...inputStyle,height:"auto" }}/></label>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginTop:16 }}><button type="button" disabled={saving} onClick={()=>{setCancelTarget(null);setCancelReason("");setPendingRequestId("");}} style={{ minHeight:48 }}>戻る／キャンセル</button><button type="button" disabled={saving || !cancelReason.trim()} onClick={cancelRecord} style={{ minHeight:48,border:0,borderRadius:8,background:"#991b1b",color:"#fff",fontWeight:900 }}>{saving ? "送信中…" : "取消する"}</button></div>
    </div></div>}
  </section>;
}
